import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LoadingScreen from '../components/shared/LoadingScreen';
import { toast } from '../utils/customToast';
import { Bounce } from 'react-toastify';
import usePageTitle from '../hooks/usePageTitle';

/**
 * Page de callback OAuth
 * Récupère le token JWT et les données utilisateur depuis l'URL
 * après l'authentification OAuth réussie
 */
const OAuthCallback = () => {
    usePageTitle('Connexion OAuth');
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();
    const [error, setError] = useState(null);

    useEffect(() => {
        const handleOAuthCallback = () => {
            try {
                // Récupérer les paramètres de l'URL
                const params = new URLSearchParams(location.search);
                const token = params.get('token');
                const userDataEncoded = params.get('user');
                const error = params.get('error');

                // Gérer les erreurs OAuth
                if (error) {
                    let errorMessage = 'Erreur lors de l\'authentification';
                    
                    switch (error) {
                        case 'auth_failed':
                            errorMessage = 'Échec de l\'authentification. Veuillez réessayer.';
                            break;
                        case 'google_auth_failed':
                            errorMessage = 'Échec de l\'authentification Google. Veuillez réessayer.';
                            break;
                        default:
                            errorMessage = `Erreur: ${error}`;
                    }
                    
                    setError(errorMessage);
                    toast.error(errorMessage, {
                        position: "top-right",
                        autoClose: 5000,
                        theme: "light",
                        transition: Bounce,
                    });
                    
                    // Rediriger vers login après 3 secondes
                    setTimeout(() => {
                        navigate('/login');
                    }, 3000);
                    return;
                }

                // Vérifier la présence du token et des données utilisateur
                if (!token || !userDataEncoded) {
                    throw new Error('Token ou données utilisateur manquants');
                }

                // Décoder les données utilisateur
                const user = JSON.parse(decodeURIComponent(userDataEncoded));

                // Sauvegarder l'email
                if (user.email) {
                    localStorage.setItem('lastEmail', user.email);
                }

                // Connecter l'utilisateur
                login(token, user);

                // Message de bienvenue
                const providerName = user.authProvider === 'google' ? 'Google' : 'OAuth';

                toast.success(`Bienvenue ${user.name} ! Connecté via ${providerName}`, {
                    position: "top-right",
                    autoClose: 5000,
                    theme: "light",
                    transition: Bounce,
                });

                // Rediriger vers la page d'accueil
                // L'AuthContext se chargera de rediriger vers /messages si l'utilisateur a un tenant actif
                navigate('/');

            } catch (err) {
                console.error('OAuth callback error:', err);
                setError('Erreur lors du traitement de l\'authentification');
                toast.error('Erreur lors du traitement de l\'authentification', {
                    position: "top-right",
                    autoClose: 5000,
                    theme: "light",
                    transition: Bounce,
                });
                
                // Rediriger vers login après 3 secondes
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            }
        };

        handleOAuthCallback();
    }, [location, navigate, login]);

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
                <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg max-w-md w-full">
                    <div className="text-center">
                        <div className="text-red-500 text-6xl mb-4">⚠️</div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                            Erreur d'authentification
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            {error}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-500">
                            Redirection vers la page de connexion...
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Écran de chargement pendant le traitement
    return <LoadingScreen />;
};

export default OAuthCallback;
